import React, { Fragment, useRef, useState, useEffect, useMemo } from 'react';
import { Button, Card, Col, Container, Form, Modal, OverlayTrigger, Row, Tooltip, Spinner } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import BootstrapTable from 'react-bootstrap-table-next';
import paginationFactory from 'react-bootstrap-table2-paginator';
import ToolkitProvider from 'react-bootstrap-table2-toolkit/dist/react-bootstrap-table2-toolkit.min';
import { serviceGetCfgForm, verDocumentoHito } from '../../../../../api/api-antecendentes-legales';
import { serviceGetHistorialAumentoConciliar, servicePostSolicitudAumentoConciliar } from '../../../../../api/api-cierre-gestion-defenza';
import { formatoMonto, formato_rut, getBase64, moneyPunto, resetFormatoMonto } from '../../../../../common/Formatos';
import { esArchivoGrande, subirArchivoGrandeS3 } from '../../../../../common/uploadArchivoGranTamano';
import { descargarArchivo } from '../../../../../common/descargarArchivo';
import { serviceObtieneParametro } from '../../../../../api/api-parametros';
import { LoadingComponent } from 'recupero-comunes';
import Mensajes from '../../../../mensajes';

const ModalSolicitudAumentoPago = ({ show, onHide, implicado, involucrado, coberturas }) => {
    const data_gestion_defensa = useSelector((state) => state.gestionDefensaReducer);
    const { llaves } = data_gestion_defensa;

    const [loading, setLoading]       = useState(false);
    const [cfg_form, setCfg_form]     = useState(null);
    const [historial, setHistorial]   = useState([]);
    const [mtoSolicitado, setMtoSolicitado] = useState('');
    const [comentario, setComentario] = useState('');
    const [file, setFile]             = useState(null);
    const [fileError, setFileError]   = useState('');
    const [formErrors, setFormErrors] = useState({});
    const [showToast, setShowToast]   = useState(false);
    const [responseData, setResponseData] = useState({});
    const [motivoRow, setMotivoRow]   = useState(null);
    const [limites, setLimites]       = useState({ liviano: 8 * 1000000, pesado: 20 * 1000000 });
    const [showConfirmGrande, setShowConfirmGrande] = useState(false);
    const [pendingFile, setPendingFile] = useState(null);
    const archivoSubidoRef = useRef({ file: null, s3Bucket: null, s3Key: null });
    const fileRef = useRef(null);

    const montoAutoConciliar = coberturas
        ? coberturas.reduce((sum, c) => sum + Number(c.montoAutoConciliar || 0), 0)
        : 0;

    const porcAumentoCalc = useMemo(() => {
        const mtoRaw = Number(resetFormatoMonto(mtoSolicitado));
        if (!mtoRaw || !montoAutoConciliar) return '';
        return ((mtoRaw - montoAutoConciliar) / montoAutoConciliar * 100).toFixed(2);
    }, [mtoSolicitado, montoAutoConciliar]);

    const tieneSolicitudPendiente = useMemo(() => historial.some(h => Number(h.codEstadoSolicitud) === 248000), [historial]);

    useEffect(() => {
        if (show) {
            setLoading(true);
            Promise.all([loadCfgForm(), loadHistorial(), loadLimites()]).finally(() => setLoading(false));
        }
    }, [show]);

    const loadCfgForm = async () => {
        const query = Buffer.from(JSON.stringify({ rutcia: llaves.idOrgCaso, idform: 98 })).toString('base64');
        const response = await serviceGetCfgForm(query);
        setCfg_form(response);
    };

    const loadLimites = async () => {
        const [liviano, pesado] = await Promise.all([serviceObtieneParametro(52), serviceObtieneParametro(53)]);
        setLimites({ liviano, pesado });
    };

    const loadHistorial = async () => {
        const query = Buffer.from(JSON.stringify({ idCaso: llaves.idCaso, idImplicado: implicado })).toString('base64');
        const result = await serviceGetHistorialAumentoConciliar(query);
        setHistorial(result || []);
    };

    const clearError = (field) => setFormErrors(prev => { const e = { ...prev }; delete e[field]; return e; });

    const handleFileChange = (e) => {
        const f = e.target.files[0];
        if (!f) return;
        if (f.size > limites.pesado) {
            setFileError(`El archivo no puede superar los ${limites.pesado / 1000000} MB`);
            setFile(null);
            if (fileRef.current) fileRef.current.value = '';
            return;
        }
        setFileError('');
        if (esArchivoGrande(f, limites.liviano)) {
            setPendingFile(f);
            setShowConfirmGrande(true);
        } else {
            setFile(f);
        }
    };

    const handleCancelArchivoGrande = () => {
        setPendingFile(null);
        if (fileRef.current) fileRef.current.value = '';
        setShowConfirmGrande(false);
    };

    const handleConfirmArchivoGrande = () => {
        setFile(pendingFile);
        setShowConfirmGrande(false);
    };

    const validate = () => {
        const errors = {};
        const mtoRaw = resetFormatoMonto(mtoSolicitado);
        if (!mtoSolicitado || isNaN(mtoRaw) || Number(mtoRaw) <= 0) {
            errors.mtoSolicitado = 'Requerido';
        } else if (Number(mtoRaw) <= montoAutoConciliar) {
            errors.mtoSolicitado = cfg_form?.['solaumtoconciliar-error-montomenor'] || 'El monto solicitado debe ser mayor al monto autorizado.';
        }
        if (!comentario.trim()) errors.comentario = 'Requerido';
        setFormErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const enviarSolicitud = async (body) => {
        try {
            const response = await servicePostSolicitudAumentoConciliar(body);
            setResponseData(response);
        } catch (e) {
            setResponseData({ code: -1, message: e.message });
        } finally {
            setLoading(false);
            setShowToast(true);
        }
    };

    const handleSubmit = async () => {
        if (!validate()) return;

        const body = {
            idCaso          : llaves.idCaso,
            idImplicadoCaso : implicado,
            idOrganizacion  : llaves.idOrgCaso,
            idUsuario       : llaves.idUsuario,
            montoSolicitado : Number(resetFormatoMonto(mtoSolicitado)),
            porcAumento     : Number(porcAumentoCalc),
            comentario      : comentario.trim()
        };

        setLoading(true);

        if (file) {
            if (esArchivoGrande(file, limites.liviano)) {
                try {
                    let archivoRef;
                    if (archivoSubidoRef.current.file === file) {
                        archivoRef = archivoSubidoRef.current;
                    } else {
                        archivoRef = await subirArchivoGrandeS3(file);
                        archivoSubidoRef.current = { file, ...archivoRef };
                    }
                    const bodyConArchivo = {
                        ...body,
                        filename : file.name,
                        mimetype : file.type,
                        s3Bucket : archivoRef.s3Bucket,
                        s3Key    : archivoRef.s3Key
                    };
                    enviarSolicitud(bodyConArchivo);
                } catch (err) {
                    setLoading(false);
                    setResponseData({ code: -1, message: err.message });
                    setShowToast(true);
                }
            } else {
                const base64data = await getBase64(file);
                const bodyConArchivo = {
                    ...body,
                    filename   : file.name,
                    mimetype   : file.type,
                    base64data : base64data.split(',')[1]
                };
                enviarSolicitud(bodyConArchivo);
            }
        } else {
            enviarSolicitud(body);
        }
    };

    const handleCloseToast = () => {
        setShowToast(false);
        if (responseData?.code === 1) {
            clearForm();
            setLoading(true);
            loadHistorial().finally(() => setLoading(false));
        }
    };

    const clearForm = () => {
        setMtoSolicitado('');
        setComentario('');
        setFile(null);
        setFileError('');
        setFormErrors({});
        if (fileRef.current) fileRef.current.value = '';
        archivoSubidoRef.current = { file: null, s3Bucket: null, s3Key: null };
    };

    const handleClose = () => { clearForm(); onHide(); };

    const handleVerDocumento = async (row) => {
        setLoading(true);
        const query = Buffer.from(JSON.stringify({
            idCaso         : llaves.idCaso,
            idImplicadoCaso: implicado,
            idCompania     : llaves.idOrgCaso,
            idfolioCarga   : row.idfolioCarga
        })).toString('base64');
        const response = await verDocumentoHito(query);
        setLoading(false);
        descargarArchivo(response);
    };

    const styleSecHeader  = { backgroundColor: '#4F517A', color: 'white', padding: '8px 16px', fontWeight: '600', fontSize: '14px' };
    const styleHistHeader = { backgroundColor: '#5B9BD5', color: 'white', padding: '8px 16px', fontWeight: '600', fontSize: '14px' };
    const colStyle        = { backgroundColor: '#728bb0', color: 'white' };

    const columnas = [
        {
            dataField: 'fecSolicitudFmt',
            text: cfg_form?.['solaumtoconciliar-grilla-fecsolicitud'] || 'Fec.Solicitud',
            headerStyle: colStyle, headerAlign: 'center', align: 'center'
        },
        {
            dataField: 'mtoSolicitado',
            text: cfg_form?.['solaumtoconciliar-grilla-mtosolicitado'] || 'Mto.Solicitado / %',
            headerStyle: colStyle, headerAlign: 'center', align: 'right',
            formatter: (c, row) => c ? `${formatoMonto(c)} / ${row.porcSolicitud}` : ''
        },
        {
            dataField: 'estadoSolicitud',
            text: cfg_form?.['solaumtoconciliar-grilla-estado'] || 'Estado',
            headerStyle: colStyle, headerAlign: 'center', align: 'center'
        },
        {
            dataField: 'mtoAutoConciliar',
            text: cfg_form?.['solaumtoconciliar-grilla-mtoautoconciliar'] || 'Mto.Auto.Conciliar',
            headerStyle: colStyle, headerAlign: 'center', align: 'right',
            formatter: (c) => c ? formatoMonto(c) : ''
        },
        {
            dataField: 'fecRespuestaFmt',
            text: cfg_form?.['solaumtoconciliar-grilla-fecrespuesta'] || 'Fec.Respuesta',
            headerStyle: colStyle, headerAlign: 'center', align: 'center'
        },
        {
            dataField: 'motivos',
            isDummyField: true,
            text: cfg_form?.['solaumtoconciliar-grilla-motivos'] || 'Motivos',
            headerStyle: { ...colStyle, width: '80px' },
            align: 'center',
            formatter: (_, row) => (
                <div className='d-flex justify-content-center gap-1'>
                    {(row.observacionSolicitud || row.observacionRespuesta) && (
                        <OverlayTrigger overlay={<Tooltip id={`obs-${row.idSolGestion}`}>Ver motivos</Tooltip>}>
                            <Button size='sm' variant='outline-success' onClick={() => setMotivoRow(row)}>
                                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" className="bi bi-file-earmark-text" viewBox="0 0 16 16">
                                    <path d="M5.5 7a.5.5 0 0 0 0 1h5a.5.5 0 0 0 0-1h-5zM5 9.5a.5.5 0 0 1 .5-.5h5a.5.5 0 0 1 0 1h-5a.5.5 0 0 1-.5-.5zm0 2a.5.5 0 0 1 .5-.5h2a.5.5 0 0 1 0 1h-2a.5.5 0 0 1-.5-.5z"/>
                                    <path d="M9.5 0H4a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2V4.5L9.5 0zm0 1v2A1.5 1.5 0 0 0 11 4.5h2V14a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1h5.5z"/>
                                </svg>
                            </Button>
                        </OverlayTrigger>
                    )}
                    {row.idfolioCarga > 0 && (
                        <OverlayTrigger overlay={<Tooltip id={`pdf-${row.idSolGestion}`}>Ver documento</Tooltip>}>
                            <Button size='sm' variant='outline-danger' onClick={() => handleVerDocumento(row)}>
                                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" className="bi bi-file-earmark-pdf" viewBox="0 0 16 16">
                                    <path d="M14 14V4.5L9.5 0H4a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2zM9.5 3A1.5 1.5 0 0 0 11 4.5h2V14a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1h5.5v2z"/>
                                    <path d="M4.603 14.087a.81.81 0 0 1-.438-.42c-.195-.388-.13-.776.08-1.102.198-.307.526-.568.897-.787a7.68 7.68 0 0 1 1.482-.645 19.697 19.697 0 0 0 1.062-2.227 7.269 7.269 0 0 1-.43-1.295c-.086-.4-.119-.796-.046-1.136.075-.354.274-.672.65-.823.192-.077.4-.12.602-.077a.7.7 0 0 1 .477.365c.088.164.12.356.127.538.007.188-.012.396-.047.614-.084.51-.27 1.134-.52 1.794a10.954 10.954 0 0 0 .98 1.686 5.753 5.753 0 0 1 1.334.05c.364.066.734.195.96.465.12.144.193.32.2.518.007.192-.047.382-.138.563a1.04 1.04 0 0 1-.354.416.856.856 0 0 1-.51.138c-.331-.014-.654-.196-.933-.417a5.712 5.712 0 0 1-.911-.95 11.651 11.651 0 0 0-1.997.406 11.307 11.307 0 0 1-1.02 1.51c-.292.35-.609.656-.927.787a.793.793 0 0 1-.58.029z"/>
                                </svg>
                            </Button>
                        </OverlayTrigger>
                    )}
                </div>
            )
        }
    ];

    return (
        <Fragment>
            <LoadingComponent show={loading} />
            <Modal show={show} onHide={handleClose} size='xl'>
                <Card>
                    <Card.Header className='mt-0 mb-0' as='h6' style={{ backgroundColor: '#4F517A', color: 'white' }}>
                        {cfg_form?.['solaumtoconciliar-title-modal'] || 'Solicitud Aumento de Monto a Conciliar'}
                    </Card.Header>
                    <Card.Body>

                        {/* Datos involucrado */}
                        <Container className='mb-3'>
                            <Row>
                                <Col md='2'>
                                    <Form.Label className='label mb-0'>{cfg_form?.['solaumtoconciliar-lbl-rut'] || 'Id/Nit'}</Form.Label>
                                    <br /><Form.Text>{involucrado?.rut && formato_rut(involucrado.rut)}</Form.Text>
                                </Col>
                                <Col md='3'>
                                    <Form.Label className='label mb-0'>{cfg_form?.['solaumtoconciliar-lbl-nombre'] || 'Nombre/Razon Social'}</Form.Label>
                                    <br /><Form.Text>{involucrado?.nombre}</Form.Text>
                                </Col>
                                <Col md='2'>
                                    <Form.Label className='label mb-0'>{cfg_form?.['solaumtoconciliar-lbl-tipoinvol'] || 'Tipo Involucrado'}</Form.Label>
                                    <br /><Form.Text>{involucrado?.tipo}</Form.Text>
                                </Col>
                                <Col md='2'>
                                    <Form.Label className='label mb-0'>{cfg_form?.['solaumtoconciliar-lbl-patente'] || 'Placa'}</Form.Label>
                                    <br /><Form.Text>{involucrado?.patente}</Form.Text>
                                </Col>
                                <Col md='3'>
                                    <Form.Label className='label mb-0'>{cfg_form?.['solaumtoconciliar-lbl-impllesio'] || 'Implicado Lesionado'}</Form.Label>
                                    <br /><Form.Text>{involucrado?.lesionado}</Form.Text>
                                </Col>
                            </Row>
                        </Container>

                        {/* Monto Autorizado a Conciliar */}
                        <div style={styleSecHeader}>
                            {cfg_form?.['solaumtoconciliar-title-mtoauto'] || 'Monto Autorizado a Conciliar'}
                        </div>
                        <Container className='mb-3 border border-top-0 pt-2 pb-2'>
                            {montoAutoConciliar === 0 ? (
                                <div className='text-danger py-2' style={{ fontSize: '15px' }}>
                                    {cfg_form?.['solaumtoconciliar-error-sinmonto'] || 'No existe monto autorizado a conciliar. No es posible realizar una solicitud de aumento.'}
                                </div>
                            ) : (
                                <>
                                    <Row className='fw-bold' style={{ fontSize: '13px', borderBottom: '1px solid #dee2e6', paddingBottom: '4px', marginBottom: '4px' }}>
                                        <Col md='6'>{cfg_form?.['solaumtoconciliar-col-amparo'] || 'Amparo CIA'}</Col>
                                        <Col md='3' className='text-end'>{cfg_form?.['solaumtoconciliar-col-mtoauto'] || 'Monto Autorizado'}</Col>
                                        <Col md='3' className='text-center'>{cfg_form?.['solaumtoconciliar-col-fecreg'] || 'Fecha Registro'}</Col>
                                    </Row>
                                    {(coberturas || []).map((c, i) => (
                                        <Row key={i} style={{ fontSize: '13px' }}>
                                            <Col md='6'>{c.cobertura}</Col>
                                            <Col md='3' className='text-end'>{formatoMonto(c.montoAutoConciliar)}</Col>
                                            <Col md='3' className='text-center'>{c.fechaRegistroFmt}</Col>
                                        </Row>
                                    ))}
                                </>
                            )}
                        </Container>

                        {/* Solicitar Monto a Conciliar */}
                        <div style={styleSecHeader}>
                            {cfg_form?.['solaumtoconciliar-title-solicitar'] || 'Solicitar Monto a Conciliar'}
                        </div>
                        <Container className='mb-3 border border-top-0 pt-3 pb-2'>
                            {tieneSolicitudPendiente && (
                                <div className='text-danger py-2' style={{ fontSize: '15px' }}>
                                    {cfg_form?.['solaumtoconciliar-error-solicitudpendiente'] || 'Ya existe una solicitud pendiente de atención. No es posible realizar una nueva solicitud.'}
                                </div>
                            )}
                            <Row className='mb-2'>
                                <Col md='1'>
                                    <Form.Label className='label mb-1'>{cfg_form?.['solaumtoconciliar-lbl-moneda'] || 'Moneda'}</Form.Label>
                                    <Form.Text as='div' className='mt-1'>COP</Form.Text>
                                </Col>
                                <Col md='3'>
                                    <Form.Label className='label is_required mb-1'>
                                        {cfg_form?.['solaumtoconciliar-input-mtosolicitado'] || 'Monto Solicitado'}
                                    </Form.Label>
                                    <Form.Control
                                        value={mtoSolicitado}
                                        onChange={e => { setMtoSolicitado(e.target.value); clearError('mtoSolicitado'); }}
                                        onBlur={e => {
                                            const raw = resetFormatoMonto(e.target.value);
                                            setMtoSolicitado(formatoMonto(raw));
                                            if (raw && !isNaN(raw) && Number(raw) > 0) {
                                                if (Number(raw) <= montoAutoConciliar) {
                                                    setFormErrors(prev => ({ ...prev, mtoSolicitado: cfg_form?.['solaumtoconciliar-error-montomenor'] || 'El monto solicitado debe ser mayor al monto autorizado.' }));
                                                }
                                            }
                                        }}
                                        onFocus={e => setMtoSolicitado(resetFormatoMonto(e.target.value))}
                                        onKeyPress={e => moneyPunto(e, false)}
                                        placeholder='0'
                                        isInvalid={!!formErrors.mtoSolicitado}
                                    />
                                    <Form.Control.Feedback type='invalid'>{formErrors.mtoSolicitado}</Form.Control.Feedback>
                                </Col>
                                <Col md='2'>
                                    <Form.Label className='label mb-1'>
                                        {cfg_form?.['solaumtoconciliar-lbl-porcaumento'] || '% Aumento'}
                                    </Form.Label>
                                    <Form.Control value={porcAumentoCalc} readOnly plaintext className='text-center border-bottom' />
                                </Col>
                                <Col md='6'>
                                    <Form.Label className='label mb-1'>
                                        {cfg_form?.['solaumtoconciliar-lbl-adjunto'] || 'Subir Documento'}
                                        <span className='text-muted ms-2' style={{ fontWeight: 'normal', fontSize: '12px' }}>
                                            ({`Tamaño máximo del archivo: ${limites.pesado / 1000000}MB`})
                                        </span>
                                    </Form.Label>
                                    <Form.Control
                                        type='file'
                                        ref={fileRef}
                                        onChange={handleFileChange}
                                        accept='.pdf,.png,.jpg,.jpeg'
                                    />
                                    {fileError && <Form.Text style={{ color: 'red' }}>{fileError}</Form.Text>}
                                </Col>
                            </Row>
                            <Row>
                                <Col md='12'>
                                    <Form.Label className='label is_required mb-1'>
                                        {cfg_form?.['solaumtoconciliar-input-comentario'] || 'Comentario'}
                                    </Form.Label>
                                    <Form.Control
                                        as='textarea'
                                        rows={2}
                                        value={comentario}
                                        onChange={e => { setComentario(e.target.value); clearError('comentario'); }}
                                        isInvalid={!!formErrors.comentario}
                                    />
                                    <Form.Control.Feedback type='invalid'>{formErrors.comentario}</Form.Control.Feedback>
                                </Col>
                            </Row>
                        </Container>

                        {/* Historial Solicitudes */}
                        <div style={styleHistHeader}>
                            {cfg_form?.['solaumtoconciliar-title-historial'] || 'Historial Solicitudes'}
                        </div>
                        <ToolkitProvider bootstrap4 keyField='idSolGestion' data={historial} columns={columnas}>
                            {props => (
                                <BootstrapTable
                                    {...props.baseProps}
                                    bordered={false}
                                    headerWrapperClasses='table-secondary'
                                    pagination={paginationFactory({ sizePerPage: 5 })}
                                    noDataIndication={<div className='text-center py-3'>No hay registros encontrados</div>}
                                />
                            )}
                        </ToolkitProvider>

                    </Card.Body>
                    <Card.Footer>
                        {showToast && (
                            <Mensajes
                                showToast={showToast}
                                handleCloseToast={handleCloseToast}
                                delay={5000}
                                responseData={responseData}
                            />
                        )}
                        <Container>
                            <Row>
                                <Col className='d-flex align-items-end justify-content-end m-2 p-0'>
                                    <Button className='m-2 w-auto' variant='outline-info' onClick={handleClose}>
                                        {cfg_form?.['solaumtoconciliar-btn-volver'] || 'Volver'}
                                    </Button>
                                    <Button className='m-2 w-auto' variant='danger' onClick={handleSubmit} disabled={montoAutoConciliar === 0 || tieneSolicitudPendiente || loading}>
                                        {loading && <Spinner as="span" animation="border" size="sm" className="me-2" />}
                                        {cfg_form?.['solaumtoconciliar-btn-enviar'] || 'Solicitar Monto'}
                                    </Button>
                                </Col>
                            </Row>
                        </Container>
                    </Card.Footer>
                </Card>
            </Modal>
            {/* Modal Motivos */}
            <Modal show={!!motivoRow} onHide={() => setMotivoRow(null)} size='md' centered>
                <Modal.Header closeButton style={{ backgroundColor: '#4F517A', color: 'white' }}>
                    <Modal.Title style={{ fontSize: '15px', fontWeight: '600' }}>
                        {cfg_form?.['solaumtoconciliar-title-motivos'] || 'Motivos'}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {motivoRow?.observacionSolicitud && (
                        <div className='mb-3'>
                            <div className='fw-bold mb-1' style={{ fontSize: '13px' }}>
                                {cfg_form?.['solaumtoconciliar-lbl-comentariosol'] || 'Comentario Solicitante'}
                            </div>
                            <div className='p-2 border rounded' style={{ fontSize: '13px', backgroundColor: '#f8f9fa', whiteSpace: 'pre-wrap' }}>
                                {motivoRow.observacionSolicitud}
                            </div>
                        </div>
                    )}
                    {motivoRow?.observacionRespuesta && (
                        <div>
                            <div className='fw-bold mb-1' style={{ fontSize: '13px' }}>
                                {cfg_form?.['solaumtoconciliar-lbl-observacioncia'] || 'Observación CIA'}
                            </div>
                            <div className='p-2 border rounded' style={{ fontSize: '13px', backgroundColor: '#f8f9fa', whiteSpace: 'pre-wrap' }}>
                                {motivoRow.observacionRespuesta}
                            </div>
                        </div>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant='outline-secondary' size='sm' onClick={() => setMotivoRow(null)}>
                        {cfg_form?.['solaumtoconciliar-btn-cerrar'] || 'Cerrar'}
                    </Button>
                </Modal.Footer>
            </Modal>

            <Modal show={showConfirmGrande} onHide={handleCancelArchivoGrande} size='md' centered>
                <Card>
                    <Card.Header className='mt-0 mb-0' as='h6' style={{ backgroundColor: '#e8761a', color: 'white' }}>
                        Confirmación
                    </Card.Header>
                    <Card.Body>
                        {`El archivo seleccionado supera los ${limites.liviano / 1000000}MB, por lo que la carga puede demorar más de lo habitual. ¿Desea continuar?`}
                    </Card.Body>
                    <Card.Footer style={{ display: 'flex', justifyContent: 'center' }}>
                        <Button className='m-2 w-auto' variant='outline-info' onClick={handleCancelArchivoGrande}>No</Button>
                        <Button className='m-2 w-auto' variant='danger' onClick={handleConfirmArchivoGrande}>Sí</Button>
                    </Card.Footer>
                </Card>
            </Modal>

            <style type='text/css'>{`
                .label { font-weight: bold; font-size: 14px; }
                .is_required:after { content: "*"; margin-left: 2px; color: #000; font-weight: 500; font-size: 12pt; }
            `}</style>
        </Fragment>
    );
};

export default ModalSolicitudAumentoPago;
